/** Chart colours. Structural colours (accent/text/grid) are read from the
 * brand design tokens at runtime so the charts re-skin with the rest of the
 * app; the categorical series colours are a fixed, brand-aligned qualitative
 * set (data encoding, not chrome). A fallback keeps this pure-callable in
 * tests / SSR where the DOM tokens are absent. */

export type ChartPalette = {
  accent: string
  conversations: string
  turns: string
  users: string
  median: string
  text: string
  grid: string
  muted: string
  unused: string
  /** Card background — used as the separator line between treemap tiles. */
  surface: string
  /** Positive / negative feedback series. */
  success: string
  error: string
  /** Background band behind Saturday/Sunday columns in the daily charts —
   * annotation chrome, so a low-alpha brand tint that sits behind the data. */
  weekend: string
  categorical: string[]
}

const FALLBACK: ChartPalette = {
  accent: '#003848',
  conversations: '#003848',
  turns: '#1c7c8c',
  users: '#b8860b',
  median: '#7c3a2d',
  text: '#1a1a1a',
  grid: '#e3e8ea',
  muted: '#8a9ba0',
  unused: '#c9d2d5',
  surface: '#ffffff',
  success: '#098246',
  error: '#c0392b',
  weekend: 'rgba(0, 56, 72, 0.07)',
  categorical: [
    '#003848',
    '#1c7c8c',
    '#b8860b',
    '#7c3a2d',
    '#4a7c59',
    '#5b6c9c',
    '#a05195',
    '#d18a3e',
  ],
}

function cssVar(styles: CSSStyleDeclaration, name: string, fallback: string): string {
  const value = styles.getPropertyValue(name).trim()
  return value === '' ? fallback : value
}

/** Reads structural colours from the document's CSS custom properties.
 * Returns the fallback verbatim when there is no DOM (tests, SSR). */
export function readChartPalette(root: HTMLElement | null = documentRoot()): ChartPalette {
  if (root === null) return FALLBACK
  const styles = getComputedStyle(root)
  return {
    ...FALLBACK,
    accent: cssVar(styles, '--color-accent', FALLBACK.accent),
    conversations: cssVar(styles, '--color-accent', FALLBACK.conversations),
    text: cssVar(styles, '--color-fg', FALLBACK.text),
    grid: cssVar(styles, '--color-border', FALLBACK.grid),
    surface: cssVar(styles, '--color-surface', FALLBACK.surface),
    success: cssVar(styles, '--color-success', FALLBACK.success),
    error: cssVar(styles, '--color-error', FALLBACK.error),
  }
}

function documentRoot(): HTMLElement | null {
  if (typeof document === 'undefined') return null
  return document.documentElement
}
